import { hasReducedMotion } from "../utils/a11y";

interface AnimatedTextItemInterface {
	element: HTMLElement;
	end: number;
	progress: number;
	start: number;
}

interface AnimatedTextsInterface {
	container: HTMLElement;
	items: AnimatedTextItemInterface[];
}

const blocks = new Map<HTMLElement, AnimatedTextsInterface>();
const activeBlocks = new Set<AnimatedTextsInterface>();
const reducedMotionMediaQuery = window.matchMedia(
	"(prefers-reduced-motion: reduce)",
);

let animationFrame = 0;
let intersectionObserver: IntersectionObserver | null = null;
let resizeObserver: ResizeObserver | null = null;
let accessibilityObserver: MutationObserver | null = null;

const clamp = (value: number): number => Math.min(1, Math.max(0, value));

const setProgress = (block: AnimatedTextsInterface, progress: number): void => {
	block.items.forEach((item: AnimatedTextItemInterface): void => {
		const itemProgress = clamp(
			(progress - item.start) / (item.end - item.start),
		);

		if (Math.abs(item.progress - itemProgress) < 0.001) {
			return;
		}

		item.progress = itemProgress;
		item.element.style.setProperty(
			"--animated-text-fill",
			`${(itemProgress * 100).toFixed(2)}%`,
		);
	});
};

const updateBlock = (block: AnimatedTextsInterface): void => {
	if (hasReducedMotion() || reducedMotionMediaQuery.matches) {
		setProgress(block, 1);
		return;
	}

	const rect = block.container.getBoundingClientRect();
	const start = window.innerHeight * 0.8;
	const end = window.innerHeight * 0.4;
	const scrollDistance = rect.height + start - end;
	const progress = clamp((start - rect.top) / scrollDistance);

	setProgress(block, progress);
};

const scheduleUpdate = (): void => {
	if (animationFrame) {
		return;
	}

	animationFrame = window.requestAnimationFrame((): void => {
		animationFrame = 0;
		activeBlocks.forEach(updateBlock);
	});
};

const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
	entries.forEach((entry: IntersectionObserverEntry): void => {
		const block = blocks.get(entry.target as HTMLElement);

		if (!block) {
			return;
		}

		if (entry.isIntersecting) {
			activeBlocks.add(block);
		} else {
			activeBlocks.delete(block);
			setProgress(block, entry.boundingClientRect.bottom <= 0 ? 1 : 0);
		}
	});

	scheduleUpdate();
};

const initObservers = (): void => {
	if (intersectionObserver) {
		return;
	}

	intersectionObserver = new IntersectionObserver(handleIntersection);
	resizeObserver = new ResizeObserver(scheduleUpdate);
	accessibilityObserver = new MutationObserver(scheduleUpdate);

	accessibilityObserver.observe(document.documentElement, {
		attributeFilter: ["class", "style"],
		attributes: true,
	});

	reducedMotionMediaQuery.addEventListener("change", scheduleUpdate);
	window.addEventListener("resize", scheduleUpdate, { passive: true });
	window.addEventListener("scroll", scheduleUpdate, { passive: true });
};

const createItems = (elements: HTMLElement[]): AnimatedTextItemInterface[] => {
	const weights = elements.map((element: HTMLElement): number =>
		Math.max(element.textContent?.length ?? 0, 1),
	);
	const totalWeight = weights.reduce(
		(total: number, weight: number): number => total + weight,
		0,
	);
	let offset = 0;

	return elements.map(
		(element: HTMLElement, index: number): AnimatedTextItemInterface => {
			const start = offset / totalWeight;
			offset += weights[index];

			return {
				element,
				end: offset / totalWeight,
				progress: -1,
				start,
			};
		},
	);
};

export const initAnimatedTextsBlock = (
	el: HTMLElement,
): AnimatedTextsInterface => {
	const existingBlock = blocks.get(el);

	if (existingBlock) {
		return existingBlock;
	}

	initObservers();

	const wordElements = Array.from(
		el.querySelectorAll<HTMLElement>(".animated-text__word"),
	);
	const fallbackElements = Array.from(
		el.querySelectorAll<HTMLElement>(".animated-text"),
	);
	const block = {
		container: el,
		items: createItems(
			wordElements.length ? wordElements : fallbackElements,
		),
	};

	blocks.set(el, block);
	updateBlock(block);
	el.classList.add("animated-texts--ready");
	intersectionObserver?.observe(el);
	resizeObserver?.observe(el);

	return block;
};

export const initAnimatedTextsBlocks = (
	selectors = ".animated-texts",
): void => {
	const blockElements = document.querySelectorAll<HTMLElement>(selectors);

	blockElements.forEach(initAnimatedTextsBlock);
};
